'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { trackQuoteRequest, trackFormSubmission } from '@/lib/analytics';

interface ServiceOption {
  id: string;
  name: string;
  icon: string;
  description: string;
}

interface CargoType {
  id: string;
  name: string;
  icon: string;
}

interface FormData {
  serviceType: string;
  cargoType: string;
  origin: string;
  destination: string;
  cargoWeight: string;
  cargoValue: string;
  urgency: string;
  companyName: string;
  contactName: string;
  email: string;
  phone: string;
  additionalInfo: string;
}

const QuoteWizardClient = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    serviceType: '',
    cargoType: '',
    origin: '',
    destination: '',
    cargoWeight: '',
    cargoValue: '',
    urgency: '',
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    additionalInfo: ''
  });
  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const serviceOptions: ServiceOption[] = [
    { id: 'minerals', name: 'Minerals Transport', icon: 'CubeIcon', description: 'Precious metals & minerals' },
    { id: 'equipment', name: 'Heavy Equipment', icon: 'TruckIcon', description: 'Mining & construction machinery' },
    { id: 'personnel', name: 'Personnel Movement', icon: 'UserGroupIcon', description: 'Secure staff transport' },
    { id: 'customs', name: 'Customs Brokerage', icon: 'DocumentTextIcon', description: 'Border clearance services' }
  ];

  const cargoTypes: CargoType[] = [
    { id: 'gold', name: 'Gold & Precious Metals', icon: 'SparklesIcon' },
    { id: 'copper', name: 'Copper & Base Metals', icon: 'CubeIcon' },
    { id: 'machinery', name: 'Heavy Machinery', icon: 'WrenchScrewdriverIcon' },
    { id: 'general', name: 'General Cargo', icon: 'ArchiveBoxIcon' }
  ];

  const countries = ['DRC', 'Kenya', 'Tanzania', 'Uganda', 'Rwanda', 'Burundi'];
  const urgencyLevels = ['Standard (7-14 days)', 'Express (3-5 days)', 'Critical (24-48 hours)'];

  const totalSteps = 4;

  const validateStep = (step: number): boolean => {
    const newErrors: Partial<FormData> = {};

    if (step === 1) {
      if (!formData.serviceType) newErrors.serviceType = 'Please select a service type';
      if (!formData.cargoType) newErrors.cargoType = 'Please select a cargo type';
    } else if (step === 2) {
      if (!formData.origin) newErrors.origin = 'Origin is required';
      if (!formData.destination) newErrors.destination = 'Destination is required';
      if (!formData.cargoWeight) newErrors.cargoWeight = 'Cargo weight is required';
      if (!formData.cargoValue) newErrors.cargoValue = 'Cargo value is required';
      if (!formData.urgency) newErrors.urgency = 'Urgency level is required';
    } else if (step === 3) {
      if (!formData.companyName) newErrors.companyName = 'Company name is required';
      if (!formData.contactName) newErrors.contactName = 'Contact name is required';
      if (!formData.email) newErrors.email = 'Email is required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email format';
      if (!formData.phone) newErrors.phone = 'Phone number is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const handlePrev = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;

    setIsSubmitting(true);
    
    // Track quote request conversion
    trackQuoteRequest({
      serviceType: formData.serviceType,
      cargoType: formData.cargoType,
      origin: formData.origin,
      destination: formData.destination,
      value: formData.cargoValue,
    });
    
    // Track form submission
    trackFormSubmission('quote_wizard', {
      company_name: formData.companyName,
      urgency: formData.urgency,
    });
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsSubmitting(false);
    setSubmitSuccess(true);
    setCurrentStep(4);
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  if (!isHydrated) {
    return (
      <div className="bg-card rounded-xl shadow-elevation-2 p-8 animate-pulse">
        <div className="h-8 bg-muted rounded w-1/3 mb-6"></div>
        <div className="space-y-4">
          <div className="h-20 bg-muted rounded"></div>
          <div className="h-20 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl shadow-elevation-2 overflow-hidden">
      {/* Progress Bar */}
      <div className="bg-muted p-6">
        <div className="flex items-center justify-between mb-4">
          {[1, 2, 3, 4].map((step) => (
            <div key={step} className="flex items-center flex-1">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full font-semibold transition-all duration-300 ${
                currentStep >= step 
                  ? 'bg-gradient-to-r from-golden-light via-golden-mid to-golden-dark text-primary' :'bg-white text-muted-foreground'
              }`}>
                {step}
              </div>
              {step < 4 && (
                <div className={`flex-1 h-1 mx-2 transition-all duration-300 ${
                  currentStep > step ? 'bg-gradient-to-r from-golden-light to-golden-mid' : 'bg-white'
                }`} />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-between text-sm font-body">
          <span className={currentStep >= 1 ? 'text-primary font-semibold' : 'text-muted-foreground'}>Service</span>
          <span className={currentStep >= 2 ? 'text-primary font-semibold' : 'text-muted-foreground'}>Details</span>
          <span className={currentStep >= 3 ? 'text-primary font-semibold' : 'text-muted-foreground'}>Contact</span>
          <span className={currentStep >= 4 ? 'text-primary font-semibold' : 'text-muted-foreground'}>Complete</span>
        </div>
      </div>

      {/* Form Content */}
      <div className="p-6 lg:p-8">
        {/* Step 1: Service Selection */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <div>
              <h3 className="font-headline font-bold text-2xl text-foreground mb-2">Select Your Service</h3>
              <p className="font-body text-muted-foreground">Choose the logistics service you need</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {serviceOptions.map((service) => (
                <button
                  key={service.id}
                  onClick={() => handleInputChange('serviceType', service.id)}
                  className={`p-6 rounded-lg border-2 transition-all duration-300 text-left ${
                    formData.serviceType === service.id
                      ? 'border-golden-mid bg-gradient-to-br from-golden-light/10 to-golden-dark/10 shadow-golden-md'
                      : 'border-border hover:border-golden-light hover:shadow-elevation-1'
                  }`}
                >
                  <Icon name={service.icon as any} size={32} className={formData.serviceType === service.id ? 'text-golden-mid mb-3' : 'text-muted-foreground mb-3'} />
                  <h4 className="font-headline font-semibold text-lg text-foreground mb-1">{service.name}</h4>
                  <p className="font-body text-sm text-muted-foreground">{service.description}</p>
                </button>
              ))}
            </div>
            {errors.serviceType && <p className="text-error text-sm font-body">{errors.serviceType}</p>}

            <div>
              <label className="block font-body font-semibold text-foreground mb-3">Cargo Type</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {cargoTypes.map((cargo) => (
                  <button
                    key={cargo.id}
                    onClick={() => handleInputChange('cargoType', cargo.id)}
                    className={`p-4 rounded-lg border-2 transition-all duration-300 ${
                      formData.cargoType === cargo.id
                        ? 'border-golden-mid bg-gradient-to-br from-golden-light/10 to-golden-dark/10' :'border-border hover:border-golden-light'
                    }`}
                  >
                    <Icon name={cargo.icon as any} size={24} className={formData.cargoType === cargo.id ? 'text-golden-mid mx-auto mb-2' : 'text-muted-foreground mx-auto mb-2'} />
                    <p className="font-body text-xs text-center text-foreground">{cargo.name}</p>
                  </button>
                ))}
              </div>
              {errors.cargoType && <p className="text-error text-sm font-body mt-2">{errors.cargoType}</p>}
            </div>
          </div>
        )}

        {/* Step 2: Shipment Details */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <div>
              <h3 className="font-headline font-bold text-2xl text-foreground mb-2">Shipment Details</h3>
              <p className="font-body text-muted-foreground">Provide information about your cargo</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block font-body font-semibold text-foreground mb-2">Origin Country *</label>
                <select
                  value={formData.origin}
                  onChange={(e) => handleInputChange('origin', e.target.value)}
                  className={`w-full px-4 py-3 rounded-lg border-2 font-body transition-all duration-300 ${
                    errors.origin ? 'border-error' : 'border-border focus:border-golden-mid'
                  } focus:outline-none focus:ring-2 focus:ring-golden-light/20`}
                >
                  <option value="">Select origin</option>
                  {countries.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
                {errors.origin && <p className="text-error text-sm font-body mt-1">{errors.origin}</p>}
              </div>

              <div>
                <label className="block font-body font-semibold text-foreground mb-2">Destination Country *</label>
                <select
                  value={formData.destination}
                  onChange={(e) => handleInputChange('destination', e.target.value)}
                  className={`w-full px-4 py-3 rounded-lg border-2 font-body transition-all duration-300 ${
                    errors.destination ? 'border-error' : 'border-border focus:border-golden-mid'
                  } focus:outline-none focus:ring-2 focus:ring-golden-light/20`}
                >
                  <option value="">Select destination</option>
                  {countries.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
                {errors.destination && <p className="text-error text-sm font-body mt-1">{errors.destination}</p>}
              </div>

              <div>
                <label className="block font-body font-semibold text-foreground mb-2">Cargo Weight (kg) *</label>
                <input
                  type="number"
                  value={formData.cargoWeight}
                  onChange={(e) => handleInputChange('cargoWeight', e.target.value)}
                  placeholder="e.g., 5000"
                  className={`w-full px-4 py-3 rounded-lg border-2 font-body transition-all duration-300 ${
                    errors.cargoWeight ? 'border-error' : 'border-border focus:border-golden-mid'
                  } focus:outline-none focus:ring-2 focus:ring-golden-light/20`}
                />
                {errors.cargoWeight && <p className="text-error text-sm font-body mt-1">{errors.cargoWeight}</p>}
              </div>

              <div>
                <label className="block font-body font-semibold text-foreground mb-2">Cargo Value (USD) *</label>
                <input
                  type="number"
                  value={formData.cargoValue}
                  onChange={(e) => handleInputChange('cargoValue', e.target.value)}
                  placeholder="e.g., 250000"
                  className={`w-full px-4 py-3 rounded-lg border-2 font-body transition-all duration-300 ${
                    errors.cargoValue ? 'border-error' : 'border-border focus:border-golden-mid'
                  } focus:outline-none focus:ring-2 focus:ring-golden-light/20`}
                />
                {errors.cargoValue && <p className="text-error text-sm font-body mt-1">{errors.cargoValue}</p>}
              </div>
            </div>

            <div>
              <label className="block font-body font-semibold text-foreground mb-3">Urgency Level *</label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {urgencyLevels.map((level) => (
                  <button
                    key={level}
                    onClick={() => handleInputChange('urgency', level)}
                    className={`p-4 rounded-lg border-2 transition-all duration-300 ${
                      formData.urgency === level
                        ? 'border-golden-mid bg-gradient-to-br from-golden-light/10 to-golden-dark/10' :'border-border hover:border-golden-light'
                    }`}
                  >
                    <p className="font-body font-semibold text-foreground">{level}</p>
                  </button>
                ))}
              </div>
              {errors.urgency && <p className="text-error text-sm font-body mt-2">{errors.urgency}</p>}
            </div>
          </div>
        )}

        {/* Step 3: Contact Information */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <div>
              <h3 className="font-headline font-bold text-2xl text-foreground mb-2">Contact Information</h3>
              <p className="font-body text-muted-foreground">How can we reach you?</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block font-body font-semibold text-foreground mb-2">Company Name *</label>
                <input
                  type="text"
                  value={formData.companyName}
                  onChange={(e) => handleInputChange('companyName', e.target.value)}
                  placeholder="Your company name"
                  className={`w-full px-4 py-3 rounded-lg border-2 font-body transition-all duration-300 ${
                    errors.companyName ? 'border-error' : 'border-border focus:border-golden-mid'
                  } focus:outline-none focus:ring-2 focus:ring-golden-light/20`}
                />
                {errors.companyName && <p className="text-error text-sm font-body mt-1">{errors.companyName}</p>}
              </div>

              <div>
                <label className="block font-body font-semibold text-foreground mb-2">Contact Name *</label>
                <input
                  type="text"
                  value={formData.contactName}
                  onChange={(e) => handleInputChange('contactName', e.target.value)}
                  placeholder="Your full name"
                  className={`w-full px-4 py-3 rounded-lg border-2 font-body transition-all duration-300 ${
                    errors.contactName ? 'border-error' : 'border-border focus:border-golden-mid'
                  } focus:outline-none focus:ring-2 focus:ring-golden-light/20`}
                />
                {errors.contactName && <p className="text-error text-sm font-body mt-1">{errors.contactName}</p>}
              </div>

              <div>
                <label className="block font-body font-semibold text-foreground mb-2">Email Address *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="your.email@company.com"
                  className={`w-full px-4 py-3 rounded-lg border-2 font-body transition-all duration-300 ${
                    errors.email ? 'border-error' : 'border-border focus:border-golden-mid'
                  } focus:outline-none focus:ring-2 focus:ring-golden-light/20`}
                />
                {errors.email && <p className="text-error text-sm font-body mt-1">{errors.email}</p>}
              </div>

              <div>
                <label className="block font-body font-semibold text-foreground mb-2">Phone Number *</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="+243981944976"
                  className={`w-full px-4 py-3 rounded-lg border-2 font-body transition-all duration-300 ${
                    errors.phone ? 'border-error' : 'border-border focus:border-golden-mid'
                  } focus:outline-none focus:ring-2 focus:ring-golden-light/20`}
                />
                {errors.phone && <p className="text-error text-sm font-body mt-1">{errors.phone}</p>}
              </div>
            </div>

            <div>
              <label className="block font-body font-semibold text-foreground mb-2">Additional Information</label>
              <textarea
                value={formData.additionalInfo}
                onChange={(e) => handleInputChange('additionalInfo', e.target.value)}
                placeholder="Any special requirements or additional details..."
                rows={4}
                className="w-full px-4 py-3 rounded-lg border-2 border-border focus:border-golden-mid font-body transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-golden-light/20 resize-none"
              />
            </div>
          </div>
        )}

        {/* Step 4: Success */}
        {currentStep === 4 && submitSuccess && (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-gradient-to-br from-success/20 to-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="CheckCircleIcon" size={48} className="text-success" />
            </div>
            <h3 className="font-headline font-bold text-3xl text-foreground mb-4">Quote Request Submitted!</h3>
            <p className="font-body text-lg text-muted-foreground mb-6 max-w-md mx-auto">
              Thank you for your interest. Our logistics experts will review your request and contact you within 2 hours.
            </p>
            <div className="bg-muted rounded-lg p-6 max-w-md mx-auto mb-8">
              <p className="font-body text-sm text-muted-foreground mb-2">Reference Number</p>
              <p className="font-mono font-bold text-2xl text-primary">MLD-{new Date().getFullYear()}-{Math.floor(Math.random() * 10000).toString().padStart(4, '0')}</p>
            </div>
            <button
              onClick={() => {
                setCurrentStep(1);
                setFormData({
                  serviceType: '',
                  cargoType: '',
                  origin: '',
                  destination: '',
                  cargoWeight: '',
                  cargoValue: '',
                  urgency: '',
                  companyName: '',
                  contactName: '',
                  email: '',
                  phone: '',
                  additionalInfo: ''
                });
                setSubmitSuccess(false);
              }}
              className="px-8 py-3 bg-gradient-to-r from-golden-light via-golden-mid to-golden-dark text-primary font-cta font-semibold rounded-lg shadow-golden-md hover:shadow-golden-lg transition-all duration-300 hover:-translate-y-0.5"
            >
              Submit Another Quote
            </button>
          </div>
        )}

        {/* Navigation Buttons */}
        {currentStep < 4 && (
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
            <button
              onClick={handlePrev}
              disabled={currentStep === 1}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-body font-semibold transition-all duration-300 ${
                currentStep === 1
                  ? 'bg-muted text-muted-foreground cursor-not-allowed'
                  : 'bg-muted text-foreground hover:bg-muted/80'
              }`}
            >
              <Icon name="ArrowLeftIcon" size={20} />
              <span>Previous</span>
            </button>

            {currentStep < 3 ? (
              <button
                onClick={handleNext}
                className="flex items-center space-x-2 px-8 py-3 bg-gradient-to-r from-golden-light via-golden-mid to-golden-dark text-primary font-cta font-semibold rounded-lg shadow-golden-md hover:shadow-golden-lg transition-all duration-300 hover:-translate-y-0.5"
              >
                <span>Next Step</span>
                <Icon name="ArrowRightIcon" size={20} />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="flex items-center space-x-2 px-8 py-3 bg-gradient-to-r from-golden-light via-golden-mid to-golden-dark text-primary font-cta font-semibold rounded-lg shadow-golden-md hover:shadow-golden-lg transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <span>Submit Quote Request</span>
                    <Icon name="PaperAirplaneIcon" size={20} />
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuoteWizardClient;